import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Force-unbind confirmation dialog (AUT-126). The force-unbind endpoint
 * takes an EMPTY body (the action itself is the forensic signal), so this
 * dialog collects no input — it just confirms the destructive
 * `recurring_unbind` intent before the parent opens the TOTP step-up.
 *
 * Only ever opened for an `active` card (the row action is status-gated).
 * The backend remains the authority on the actual unbind, including the
 * `card_has_pending_payouts` 409 (mapped to a card-specific toast in
 * `useForceUnbind`) — this dialog only gates the click. `last4` is shown so
 * the operator confirms WHICH card; never a full PAN.
 */
export interface ForceUnbindDialogProps {
  open: boolean;
  /** Masked tail of the card being unbound (display only — never full PAN). */
  last4: string;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ForceUnbindDialog = ({
  open,
  last4,
  loading = false,
  onConfirm,
  onCancel,
}: ForceUnbindDialogProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('cards.forceUnbindDialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('cards.forceUnbindDialog.description', { last4 })}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={loading}
          loading={loading}
          onClick={() => {
            if (!loading) onConfirm();
          }}
        >
          {t('cards.forceUnbindDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
